import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NewCustomDialog, NewDialogProps } from "../../components/dialog/NewCustomDialog";
import { AppContext } from "../../context";
import { useNavigate } from "react-router-dom";
import { useTransactions } from "../../hooks/useTransactions";
import { Header } from "../../components";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { REVALIDATE_FORM_PROPS, TRX_BENEFICIARY_OPTIONS, TRX_STATES_OPTIONS, TRX_TYPE_OPTIONS, infoTransactionsFields } from "../../constants";
import { TransactionsFormSchema } from "../../validations/schemas";
import { useVersions } from "../../hooks/useVersions";
import { useTranslations } from "../../hooks/useTranslations";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface NewTransactionProps {
  update?: boolean;
}

const transactionDefaultValues = {
  name: "",
  systemfunction: "",
  labelkey: "",
  wildcardcode: "",
  authorizationflowname: "",
  type: "",
  beneficiaryregistrationtype: " ",
  isbatch: false,
  releaseversion: "",
  fieldorder: "",
  status: "",
};

export const NewTransactionPage: React.FC<NewTransactionProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedTransaction },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update ? 
        {
          id: selectedTransaction?.id,
          name: selectedTransaction?.name,
          systemfunction: selectedTransaction?.systemfunction,
          labelkey: selectedTransaction?.labelkey,
          wildcardcode: selectedTransaction?.wildcardcode,
          authorizationflowname: selectedTransaction?.authorizationflowname,
          type: selectedTransaction?.type,
          beneficiaryregistrationtype: selectedTransaction?.beneficiaryregistrationtype,
          fieldorder: selectedTransaction?.fieldorder,
          isbatch: selectedTransaction?.isbatch,
          releaseversion: selectedTransaction?.releaseversion,
          status: selectedTransaction?.status,
        }
      : 
        {
          ...transactionDefaultValues
        },
  });
  const {
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid },
  } = form;

  const {
    transactionAction,
  } = useTransactions("DATA");
  const { data: versionsData } = useVersions("DATA");
  const { data: labelsData, reloadFn } = useTranslations("DATA");
  const [transactionsFields, setTransactionsFields] = useState([]);
  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const formKeysOptions = useMemo(() => {
    if (versionsData && labelsData) {
      return {
        status: TRX_STATES_OPTIONS(t),
        type: TRX_TYPE_OPTIONS(t),
        beneficiaryregistrationtype: TRX_BENEFICIARY_OPTIONS(t),
        releaseversion: versionsData
          .map((version: any) => ({
            label: version?.name,
            value: version?.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
        labelkey: (()=>{
            let labelsOptions = labelsData?.filter((item: any) => item?.widgets)
              ?.map((item: any)=> ({ label: item?.key, value: item?.key}));
            return update ? [ ...labelsOptions, {label: selectedTransaction?.labelkey, value: selectedTransaction?.labelkey}] : labelsOptions;
          })()
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
      };
    } else {
      return;
    }
  }, [labelsData, t, versionsData]);

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await transactionAction(update ? "UPDATE" : "CREATE", 
      update ? 
        [{
          id: selectedTransaction?.id,
          ...formValues,
          updatedby: user?.name,
        }]
      :
        {
          ...formValues,
          createdby: user?.name,
        }
    );
    if (actionStatus === "SUCCESS") {
      if (!update) {
        reset(transactionDefaultValues);
        navigate(-1);
        window.scrollTo(0, 0);
      } else {
        reset(formValues);
        setAppState((prev: any) => ({
          ...prev,
          selectedTransaction: formValues,
        }));
      }
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev: any) => ({
          ...prev,
          visible: true,
          title: t(`Transactions.${update ? "save" : "create"}ModalTitle`),
          content: t(`Transactions.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Transactions.cancelModalTitle"),
          content: t("Transactions.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update) {
      const keysToExtract = ['systemfunction', 'wildcardcode', 'authorizationflowname'];
      const extractedObjects: any = infoTransactionsFields.filter(obj => !keysToExtract.includes(obj.key))
      setTransactionsFields(extractedObjects)
    } else {
      const keysToExtract = ['systemfunction', 'wildcardcode', 'authorizationflowname', 'status', 'fieldorder'];
      const extractedObjects: any = infoTransactionsFields.filter(obj => !keysToExtract.includes(obj.key));
      setTransactionsFields(extractedObjects)
    }
  }, [update]);

  return (
    <>
      {/* {!update && <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      />} */}

      {!update && <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Transactions.newTransactionTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
           id={t("Transactions.productAdminTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Transactions.transactionAdminTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update ? t("Transactions.editTransactionTitle") : t("Transactions.newTransactionTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>}

      <div id="margin-fixed-bgscreen">
        <CustomFormComponent
          functionality="Transactions"
          fields={transactionsFields}
          form={form}
          dropdownOptions={formKeysOptions}
          schemaOfValidation={TransactionsFormSchema}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          dropdownFilterObject={{ labelkey: true }}
          customComponent={createLabelButtonMaker({
            setModalProps,
            dialogTitle: t('ConsoleTranslations.createLabelTitle'),
            dialogDetail: t('ConsoleTranslations.createLabelDescription'),
            buttonLabel: t('ConsoleTranslations.createLabelTitle'),
            functionality:'Transactions',
            actionsToExecute:(key: string)=>{
              reloadFn();
              setValue("labelkey", key, REVALIDATE_FORM_PROPS)
            },
          })}
        />
      </div>

      <div id="margin-fixed-bgscreen">
        <BoxFull className="m-0 p-0" variant="tertiary">
          <div className="info_osp_title">
            <span>{t("Transactions.informationOspTitle")}</span>
          </div>
        </BoxFull>
      </div>

      <div id="margin-fixed-bgscreen">
        <CustomFormComponent
          functionality="Transactions"
          fields={infoTransactionsFields.filter((item) => item.key === 'systemfunction' || item.key === 'wildcardcode' || item.key === 'authorizationflowname' )}
          form={form}
          dropdownOptions={formKeysOptions}
          schemaOfValidation={TransactionsFormSchema}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
        />
      </div>

      <div id="margin-fixed-bgscreen">
        <BoxFull className={`pt-0 px-4 ${update ? 'pb-0' : 'pb-5'}`} style={{ backgroundColor: "transparent" }}>
          <div className="flex align-items-center justify-content-end gap-5">
            <Button
              label={t("Transactions.cancelBtnLabel")}
              onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
              className="mr-4"
              variant="p-button-link"
            />
            <Button
              label={
                update
                  ? t("Transactions.saveBtnLabel")
                  : t("Transactions.createBtnLabel")
              }
              variant="p-button"
              onClick={() => handleModalProps("action")}
              disabled={update ? !(isValid && isDirty) : !isValid}
            />
          </div>
        </BoxFull>
      </div>

      <NewCustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-transactions-page-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
